import numeral from "numeral";
import { useAsync } from "react-use";
import { useTranslation } from "react-i18next";
import { useCallback, useEffect, useState } from "react";
import {
  TransferCategory,
  TransferCreate,
  TransferRoundHelper,
  TransferService,
} from "@/domains/transfer";
import { UserService } from "@/domains/user";
// components
import { Button, Checkbox, Divider, Form, Input } from "antd";

export const TenantManageCreateMemberDepositPage = () => {
  const { t } = useTranslation();

  const [form] = Form.useForm<TransferCreate>();

  const [loading, setLoading] = useState(false);

  const [resTargetAddress, setResTargetAddress] = useState("");

  const [isSyncToAmount, setIsSyncToAmount] = useState(true);

  const [isSyncToAmount0, setIsSyncToAmount0] = useState(false);

  const deposit_rate = useAsync(async () => {
    const res = await UserService.getMyRates().run();
    return res.data.msg.deposit_rate;
  });

  const deposit_round = useAsync(async () => {
    const res = await UserService.getMyRounds().run();
    return res.data.msg.deposit_round;
  });

  const isDepositRateRequired =
    deposit_rate.value !== undefined && parseFloat(deposit_rate.value) !== 1;

  const handleAmount0Input = useCallback(
    (value: string) => {
      if (!isSyncToAmount || !deposit_round.value) return;

      if (Number.isNaN(parseFloat(value))) {
        form.setFieldValue("amount", "");
        return;
      }

      const amount = numeral(value).divide(deposit_rate.value).value();
      if (amount === null) return;

      const newAmount = TransferRoundHelper.round(amount, deposit_round.value);
      form.setFieldValue("amount", newAmount);
    },
    [form, isSyncToAmount, deposit_rate.value, deposit_round.value]
  );

  const handleAmountInput = useCallback(
    (value: string) => {
      if (!isSyncToAmount0 || !isDepositRateRequired) return;

      if (Number.isNaN(parseFloat(value))) {
        form.setFieldValue("amount0", "");
        return;
      }

      const amount0 = numeral(value).multiply(deposit_rate.value).value();
      if (amount0 === null) return;

      form.setFieldValue("amount0", Math.ceil(amount0));
    },
    [form, isSyncToAmount0, isDepositRateRequired, deposit_rate.value]
  );

  const handleFinish = async (values: TransferCreate) => {
    const data = {
      ...values,
      currency: "USDT",
    };

    try {
      setLoading(true);
      const res = await TransferService.create({
        ...data,
        category: TransferCategory.MemberDeposit,
        currency: "USDT",
      }).run();
      const address = res.data.msg.transfer.target?.address;
      address && setResTargetAddress(address);
    } catch (e) {
      console.log(e);
    } finally {
      setLoading(false);
    }
  };

  const handleReset = () => {
    setResTargetAddress("");
  };

  useEffect(() => {
    const field = form.getFieldInstance("amount0");
    if (!field) return;
    const input = form.getFieldInstance("amount0").input as HTMLInputElement;
    handleAmount0Input(input.value);
  }, [form, handleAmount0Input]);

  useEffect(() => {
    const field = form.getFieldInstance("amount");
    if (!field) return;
    const input = form.getFieldInstance("amount").input as HTMLInputElement;
    handleAmountInput(input.value);
  }, [form, handleAmountInput]);

  const Amount0FormItem = () => (
    <Form.Item
      label={t("domains.Transfer.depositAmount")}
      rules={[
        {
          required: true,
          message: t("systems.Error.cannotBeEmpty", {
            field: t("domains.Transfer.depositAmount"),
          }),
        },
      ]}
      name="amount0"
    >
      <Input
        type="number"
        placeholder={t("systems.Placeholder.pleaseInput", {
          field: t("domains.Transfer.depositAmount"),
        })}
        onInput={(e) =>
          handleAmount0Input((e.target as HTMLInputElement).value)
        }
        suffix={
          <Checkbox
            checked={isSyncToAmount}
            onChange={(e) => setIsSyncToAmount(e.target.checked)}
          >
            {t("domains.Transfer.autoSyncToDepositUnit")}
          </Checkbox>
        }
      />
    </Form.Item>
  );

  const RateFormItem = () => (
    <Form.Item
      label={t("domains.Transfer.depositRate")}
      name="rate"
      initialValue={deposit_rate.value}
    >
      <Input readOnly type="number" />
    </Form.Item>
  );

  return (
    <div className="w:500 p:16 bg:white r:4 mt:0 mb:0 ml:auto mr:auto">
      <h4 className="mb:24 t:center fg:secondary">
        {t("systems.Menu.createMemberDeposit")}
      </h4>

      <Form
        form={form}
        labelCol={{ span: 5 }}
        onFinish={handleFinish}
        onReset={handleReset}
      >
        {isDepositRateRequired && <Amount0FormItem />}
        {isDepositRateRequired && <RateFormItem />}

        <Form.Item
          label={t("domains.Transfer.depositUnit")}
          rules={[{ required: true }]}
          name="amount"
        >
          <Input
            type="number"
            placeholder={t("systems.Placeholder.pleaseInput", {
              field: t("domains.Transfer.depositUnit"),
            })}
            onInput={(e) =>
              handleAmountInput((e.target as HTMLInputElement).value)
            }
            suffix={
              isDepositRateRequired && (
                <Checkbox
                  checked={isSyncToAmount0}
                  onChange={(e) => setIsSyncToAmount0(e.target.checked)}
                >
                  {t("domains.Transfer.autoSyncToDepositAmount")}
                </Checkbox>
              )
            }
          />
        </Form.Item>

        <Form.Item
          label={t("domains.Transfer.orderId")}
          name={["order", "id"]}
          rules={[
            {
              required: true,
              message: t("systems.Error.cannotBeEmpty", {
                field: t("domains.Transfer.orderId"),
              }),
            },
          ]}
        >
          <Input
            placeholder={t("systems.Placeholder.pleaseInput", {
              field: t("domains.Transfer.orderId"),
            })}
          />
        </Form.Item>

        <Form.Item
          label={t("domains.Transfer.memberId")}
          name={["member", "id"]}
        >
          <Input
            placeholder={t("systems.Placeholder.pleaseInput", {
              field: t("domains.Transfer.memberId"),
            })}
          />
        </Form.Item>

        <Form.Item
          label={t("domains.Transfer.memberUsername")}
          name={["member", "username"]}
        >
          <Input
            placeholder={t("systems.Placeholder.pleaseInput", {
              field: t("domains.Transfer.memberUsername"),
            })}
          />
        </Form.Item>

        <Form.Item
          label={t("domains.Transfer.memberName")}
          name={["member", "name"]}
        >
          <Input
            placeholder={t("systems.Placeholder.pleaseInput", {
              field: t("domains.Transfer.memberName"),
            })}
          />
        </Form.Item>

        <Form.Item label={t("domains.Transfer.note")} name="note">
          <Input
            placeholder={t("systems.Placeholder.pleaseInput", {
              field: t("domains.Transfer.note"),
            })}
          />
        </Form.Item>

        <div className="flex jc:center gap:16">
          <Button type="primary" htmlType="submit" loading={loading}>
            {t("systems.Action.submit")}
          </Button>

          <Button htmlType="reset">{t("systems.Action.clear")}</Button>
        </div>

        {resTargetAddress && (
          <>
            <Divider />

            <Form.Item label={t("domains.Transfer.targetAddress")}>
              <Input readOnly value={resTargetAddress} />
            </Form.Item>
          </>
        )}
      </Form>
    </div>
  );
};
